import Joyride, { STATUS, EVENTS, CallBackProps, Status } from 'react-joyride';
import { useState, useEffect } from 'react';
import { useTheme } from 'styled-components';
import { LOCALE_TRANSLATE_JOYRIDE } from '../../constants';
import { TutorialType } from '../../types/enums';
import { UserController } from '../../controller/UserController';
import { useAuth } from '../../context';
import { useTutorialProgress } from '../../context/tutorialProgress';

export interface StepProps {
  target: string;
  content: string;
  delay?: number;
  disableBeacon?: boolean;
}

interface JoyrideProps {
  steps: StepProps[];
  run: boolean;
  type: TutorialType;
  onCallbackFinished: (status: Status) => void;
  onStepChange?: (target: string) => void;
}

const waitForElement = (selector: string, timeout: number): Promise<void> => {
  return new Promise((resolve) => {
    const intervalTime = 100;
    const interval = setInterval(() => {
      if (document.querySelector(selector)) {
        clearInterval(interval);
        resolve();
      }
    }, intervalTime);
    setTimeout(() => {
      clearInterval(interval);
      resolve();
    }, timeout);
  });
};

export default function JoyrideTour({
  run,
  steps,
  onCallbackFinished,
  type,
  onStepChange,
}: JoyrideProps) {
  const { colors } = useTheme();
  const { setUser, user, tokenIsValid } = useAuth();
  const { tutorialProgress, setTutorialProgress } = useTutorialProgress();
  const [stepIndex, setStepIndex] = useState(0);
  const [isRunning, setIsRunning] = useState(run);

  const updateTutorialProgress = async () => {
    if (
      tokenIsValid &&
      (!user?.tutorialProgress || !user?.tutorialProgress?.some((t) => t === type))
    ) {
      await UserController.completeTutorialType(type).then(() => {
        setUser({
          ...user,
          tutorialProgress:
            Array.isArray(user?.tutorialProgress) && user.tutorialProgress.some((t) => t === type)
              ? user.tutorialProgress
              : [...(user?.tutorialProgress ?? []), type],
        });
      });
    }
    if (!tutorialProgress.some((t) => t === type)) {
      setTutorialProgress([...tutorialProgress, type]);
    }
  };
  const handleJoyrideCallback = async (data: CallBackProps) => {
    const { index, type: typeEvent, status, action } = data;

    if (status === STATUS.SKIPPED) {
      await updateTutorialProgress();
      onCallbackFinished(status);
      setIsRunning(false);
      return;
    }

    if (typeEvent === EVENTS.STEP_AFTER && action === 'next') {
      const nextIndex = index + 1;
      if (nextIndex < steps.length) {
        const nextStep = steps[nextIndex];
        if (nextStep.delay) {
          setIsRunning(false);
          onStepChange && onStepChange(nextStep.target);
          await waitForElement(nextStep.target, nextStep.delay);
          setStepIndex(nextIndex);
          setIsRunning(true);
        } else {
          setStepIndex(nextIndex);
          onStepChange && onStepChange(steps[nextIndex].target);
        }
      } else {
        await updateTutorialProgress();
        onCallbackFinished(STATUS.FINISHED);
        setIsRunning(false);
      }
    } else if (typeEvent === EVENTS.STEP_AFTER && action === 'prev') {
      const prevIndex = index - 1;
      if (prevIndex >= 0) {
        setStepIndex(prevIndex);
        onStepChange && onStepChange(steps[prevIndex].target);
      }
    }
  };

  useEffect(() => {
    if (run) {
      setStepIndex(0);
      setIsRunning(true);
    }
  }, [run]);

  return (
    <Joyride
      steps={steps}
      stepIndex={stepIndex}
      run={isRunning}
      continuous
      showProgress
      disableCloseOnEsc
      hideCloseButton
      disableOverlayClose
      showSkipButton
      callback={handleJoyrideCallback}
      locale={LOCALE_TRANSLATE_JOYRIDE}
      styles={{
        options: {
          zIndex: 9999,
          overlayColor: 'rgba(0, 0, 0, 0.5)',
        },
        buttonNext: {
          backgroundColor: colors.primary,
        },
        buttonBack: {
          color: colors.secondary,
        },
        beaconInner: {
          background: colors.tertiary_500,
        },
        beaconOuter: {
          background: colors.primary_100,
          borderColor: colors.tertiary_500,
        },
        beacon: {
          background: colors.primary_100,
          border: colors.primary_100,
        },
      }}
    />
  );
}
